import type { v2 } from '@deque/billing-service-client';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductSlugs } from '@deque/billing-utils';
import { type AuthUser } from '~/common/contexts/auth';
import { IntegrationLogo } from '../../../integrations/IntegrationLogo';
import { type ProductSlugWithConnections } from '../IntegrationConfigurations';
import styles from '../IntegrationConfigurations.css';
import { CustomConnectionButtonsLine } from './CustomConnectionButtonsLine';

export interface CustomIntegrationConfigurationsProps {
  connectionByProductSlug: ProductSlugWithConnections;
  activeEnterprise: v2.Enterprise;
  isEnterpriseAdmin: boolean;
  authUser: AuthUser;
  reloadConnections: () => void;
}

function CustomIntegrationConfigurations({
  connectionByProductSlug,
  activeEnterprise,
  isEnterpriseAdmin,
  authUser,
  reloadConnections
}: CustomIntegrationConfigurationsProps) {
  const { t } = useTranslation();

  return (
    <Fragment>
      <div className={styles.productItemWrap}>
        <div className={styles.productLogo}>
          <IntegrationLogo
            integrationProductSlug={ProductSlugs.customIntegration}
          />
        </div>

        <div className={styles.connectionItemDescription}>
          <h3>{connectionByProductSlug.integrationProductInfo.displayName}</h3>
          <p>
            {t(
              'Boost your team’s accessibility efforts by integrating directly into your organization’s tooling via webhooks'
            )}
          </p>
          {connectionByProductSlug.connections.map(connection => (
            <div className={styles.connectionItemWrap} key={connection.id}>
              <h4>{connection.name}</h4>
              <CustomConnectionButtonsLine
                key={connection.id}
                connection={connection}
                integrationProductInfo={
                  connectionByProductSlug.integrationProductInfo
                }
                enterprise={activeEnterprise}
                canManageIntegrations={isEnterpriseAdmin}
                authUser={authUser}
                connectionsUpdated={reloadConnections}
              />
            </div>
          ))}
        </div>
      </div>

      {!connectionByProductSlug.connections.length && (
        <CustomConnectionButtonsLine
          key={`new-custom-connection`}
          connection={null}
          integrationProductInfo={
            connectionByProductSlug.integrationProductInfo
          }
          enterprise={activeEnterprise}
          canManageIntegrations={isEnterpriseAdmin}
          authUser={authUser}
          connectionsUpdated={reloadConnections}
        />
      )}
    </Fragment>
  );
}

export default CustomIntegrationConfigurations;
